import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card,
    Table,
    DatePicker,
    Input,
    Button,
    Space,
    Row,
    Col,
    Alert,
    Dropdown,
    Typography,
    message,
    Pagination,
    Modal,
    Select,
    InputNumber,
    Tooltip,
    ConfigProvider,
    theme
} from 'antd';
import {
    SearchOutlined,
    DownloadOutlined,
    DeleteOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    BarChartOutlined,
    EyeOutlined,
    EditOutlined,
    FileTextOutlined,
    CalculatorOutlined,
    CheckOutlined,
    PlayCircleOutlined,
    CloseCircleOutlined,
    MoreOutlined,
    InfoCircleOutlined,
    ExportOutlined,
    ScissorOutlined,
    StopOutlined,
    PlusOutlined,
    FilterOutlined,
    ClearOutlined,
    CaretUpOutlined,
    CaretDownOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { getNumericFilters, getStringFilters, getDateFilters, formatCellValue, isDateExpired, isDateApproaching, applyAdvancedFilter, redirectToPivotWithData, exportToExcel, printTable } from '../../utils/tableFilters';
import { trIncludes } from '../../utils/trText';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import * as XLSX from 'xlsx';
import FormikForm from '../form/FormikForm';
import { useNavigate, useLocation } from 'react-router-dom';
import '../insaat/KurulumHareketleriPage.css';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const iconMap = {
    'EyeOutlined': EyeOutlined,
    'EditOutlined': EditOutlined,
    'FileTextOutlined': FileTextOutlined,
    'DownloadOutlined': DownloadOutlined,
    'CalculatorOutlined': CalculatorOutlined,
    'CheckOutlined': CheckOutlined,
    'PlayCircleOutlined': PlayCircleOutlined,
    'CloseCircleOutlined': CloseCircleOutlined,
    'InfoCircleOutlined': InfoCircleOutlined,
    'ExportOutlined': ExportOutlined,
    'ScissorOutlined': ScissorOutlined,
    'StopOutlined': StopOutlined,
    'MoreOutlined': MoreOutlined,
    'DeleteOutlined': DeleteOutlined,
    'PlusOutlined': PlusOutlined,
    'SearchOutlined': SearchOutlined,
};

const ActionButtons = ({ record, actionButtons, darkMode }) => {
    if (!actionButtons || actionButtons.length === 0) {
        return null;
    }

    if (actionButtons.length > 2) {
        const menuItems = actionButtons.map(button => {
            const IconComponent = iconMap[button.icon];
            return {
                key: button.key,
                label: button.label,
                icon: IconComponent ? <IconComponent /> : null,
                onClick: () => button.onClick(record)
            };
        });

        return (
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                placement="bottomRight"
            >
                <Button
                    type="text"
                    icon={<MoreOutlined />}
                    size="small"
                    className={darkMode ? 'dark-button' : ''}
                    style={{
                        color: darkMode ? 'var(--dark-text)' : undefined
                    }}
                />
            </Dropdown>
        );
    }

    return (
        <Space size="small">
            {actionButtons.map(button => {
                const IconComponent = iconMap[button.icon];
                return (
                    <Button
                        key={button.key}
                        type={button.type}
                        size="small"
                        icon={IconComponent ? <IconComponent /> : null}
                        onClick={() => button.onClick(record)}
                        className={darkMode ? 'dark-button' : ''}
                        style={{
                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                            color: darkMode ? 'var(--dark-text)' : undefined
                        }}
                    >
                        {button.label}
                    </Button>
                );
            })}
        </Space>
    );
};

const SatilanAraclarPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { darkMode } = useTheme();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        dateRange: null,
        search: '',
        page: 1,
        pageSize: 50,
    });
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
        pageSize: 10
    });
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState(null);
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailData, setDetailData] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [currentRecordId, setCurrentRecordId] = useState(null);
    const [modalButtonType, setModalButtonType] = useState('main-table');
    const [currentKeyName, setCurrentKeyName] = useState('araclar-satilan');
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [columns, setColumns] = useState([]);
    const [showData, setShowData] = useState([]);

    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const keyName = urlParams.get('key_name');
        if (keyName) {
            setCurrentKeyName(keyName);
        }
    }, [location.search]);

    const getDataConfig = (keyName) => {
        const configs = {
            'araclar-satilan': {
                key: 'araclar-satilan',
                label: 'Satılan Araçlar',
                sqlKey: 'araclar-satilan',
                formManagerId: 29,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'edit',
                            label: 'Düzenle',
                            type: 'default',
                            icon: 'EditOutlined',
                            onClick: (record) => {
                                const id = record.key_araclar;
                                if (id) {
                                    handleOpenForm(131, 'Araç Özellikleri', 'araclar', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'delete',
                            label: 'Sil',
                            type: 'default',
                            icon: 'DeleteOutlined',
                            onClick: async (record) => {
                                const id = record.key_araclar;
                                try {
                                    const response = await fetch(`${API_BASE_URL}/list/araclar/update-active`, {
                                        method: 'PUT',
                                        headers: {
                                            'Content-Type': 'application/json',
                                        },
                                        body: JSON.stringify({
                                            id: id,
                                            active: 0
                                        })
                                    });
                                    if (!response.ok) {
                                        throw new Error(`HTTP error! status: ${response.status}`);
                                    }
                                    const result = await response.json();
                                    message.success('Araç başarıyla silindi');
                                    loadData();
                                    
                                } catch (error) {
                                    console.error('Silme işlemi sırasında hata:', error);
                                    message.error('Silme işlemi başarısız: ' + error.message);
                                }
                            }
                        },
                        {
                            key: 'view',
                            label: 'Detay',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                navigate(`/arac-detay/araclar/${record.key_araclar}?source=sold`);
                            }
                        },
                        {
                            key: 'upload',
                            label: 'Yükle',
                            type: 'default',
                            icon: 'PlusOutlined',
                            onClick: (record) => {
                                const id = record.key_araclar;
                                if (id) {
                                    handleOpenForm(109, 'Araç Ruhsat Dosya Ekle', 'araclar_dosya', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'viewFile',
                            label: 'Dosya Aç',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                const id = record.key_demirbas_zimmet;
                                handleOpenDetailModal(record, null, 'sub-table');
                            }
                        }
                    ]
                }
            },
            'arac-bitmis_sigorta-liste': {
                key: 'arac-bitmis_sigorta-liste',
                label: 'Süresi Geçen Sigortalar',
                sqlKey: 'arac-bitmis_sigorta-liste',
                formManagerId: 37,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'upload',
                            label: 'Yükle',
                            type: 'default',
                            icon: 'PlusOutlined',
                            onClick: (record) => {
                                const id = record.key_araclar;
                                if (id) {
                                    handleOpenForm(109, 'Araç Ruhsat Dosya Ekle', 'araclar_dosya', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'view',
                            label: 'Detay',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                navigate(`/arac-detay/araclar/${record.key_araclar}?source=sold`);
                            }
                        }
                    ]
                }
            },
            'tum_sigortalar': {
                key: 'tum_sigortalar',
                label: 'Tüm Sigortalar',
                sqlKey: 'tum_sigortalar',
                formManagerId: 157,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'search',
                            label: 'Ara',
                            type: 'default',
                            icon: 'SearchOutlined',
                            onClick: (record) => {
                                const id = record.key_sigortadetay;
                                if (id) {
                                    navigate(`/satilan-araclar?key_name=sigorta_detay/arac&search=${id}`); 
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'edit',
                            label: 'Düzenle',
                            type: 'default',
                            icon: 'EditOutlined',
                            onClick: (record) => {
                                const id = record.key_sigortadetay;
                                if (id) {
                                    handleOpenForm(145, 'Araç Sigorta', 'arac_sigorta', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'view',
                            label: 'Detay',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                const id = record.key_sigortadetay;
                                if (id) {
                                    handleOpenForm(107, 'Araç Sigorta Dosya', 'araclar_dosya', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        }
                    ]
                }
            },
            'arac-kaza-liste': {
                key: 'arac-kaza-liste',
                label: 'Araç Kazaları',
                sqlKey: 'arac-kaza-liste',
                formManagerId: 31,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'upload',
                            label: 'Yükle',
                            type: 'default',
                            icon: 'PlusOutlined',
                            onClick: (record) => {
                                const id = record.key_kaza_liste;
                                if (id) {
                                    handleOpenForm(115, 'Araç Kaza Dosya', 'araclar_dosya', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'viewFile',
                            label: 'Dosya Aç',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                handleOpenDetailModal(record, null, 'sub-table', 'arac-kaza');
                            }
                        }
                    ]
                }
            },
            'arac-ceza-liste': {
                key: 'arac-ceza-liste',
                label: 'Araç Cezaları',
                sqlKey: 'arac-ceza-liste',
                formManagerId: 33,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'upload',
                            label: 'Yükle',
                            type: 'default',
                            icon: 'PlusOutlined',
                            onClick: (record) => {
                                const id = record.key_ceza_liste;
                                if (id) {
                                    handleOpenForm(117, 'Araç Ceza Dosya', 'araclar_dosya', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'view',
                            label: 'Görüntüle',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                handleOpenDetailModal(record, null, 'sub-table', 'arac-ceza');
                            }
                        }
                    ]
                }
            },
            'arac-ariza-liste': {
                key: 'arac-ariza-liste',
                label: 'Araç Arızaları',
                sqlKey: 'arac-ariza-liste',
                formManagerId: 34,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'upload',
                            label: 'Yükle',
                            type: 'default',
                            icon: 'PlusOutlined',
                            onClick: (record) => {
                                const id = record.key_ariza_liste;
                                if (id) {
                                    handleOpenForm(119, 'Araç Arıza Dosya', 'araclar_dosya', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'view',
                            label: 'Dosya Aç',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                handleOpenDetailModal(record, null, 'sub-table', 'arac-ariza');
                            }
                        }
                    ]
                }
            },
            'arac-sigorta-liste': {
                key: 'arac-sigorta-liste',
                label: 'Araç Sigortaları',
                sqlKey: 'arac-sigorta-liste',
                formManagerId: 35,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'upload',
                            label: 'Yükle',
                            type: 'default',
                            icon: 'PlusOutlined',
                            onClick: (record) => {
                                const id = record.No;
                                if (id) {
                                    handleOpenForm(117, 'Araç Sigorta Listesi', 'araclar_dosya', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'view',
                            label: 'Dosya Aç',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                handleOpenDetailModal(record, null, 'sub-table', 'arac-sigorta');
                            }
                        }
                    ]
                }
            },
            'arac-muayene-liste': {
                key: 'arac-muayene-liste',
                label: 'Araç Muayeneleri',
                sqlKey: 'arac-muayene-liste',
                formManagerId: 38,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'upload',
                            label: 'Yükle',
                            type: 'default',
                            icon: 'PlusOutlined',
                            onClick: (record) => {
                                const id = record.key_muayene_liste;
                                if (id) {
                                    handleOpenForm(113, 'Araç Muayene Dosya', 'araclar_dosya', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'view',
                            label: 'Dosya Aç',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                handleOpenDetailModal(record, null, 'sub-table', 'arac-muayene');
                            }
                        }
                    ]
                }
            },
            'arac-bakim-liste': {
                key: 'arac-bakim-liste',
                label: 'Araç Bakımları',
                sqlKey: 'arac-bakim-liste',
                formManagerId: 39,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'upload',
                            label: 'Yükle',
                            type: 'default',
                            icon: 'PlusOutlined',
                            onClick: (record) => {
                                const id = record.key_bakim_liste;
                                if (id) {
                                    handleOpenForm(111, 'Araç Bakım Dosya', 'araclar_dosya', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'view',
                            label: 'Dosya Aç',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                handleOpenDetailModal(record, null, 'sub-table', 'arac-bakim');
                            }
                        }
                    ]
                }
            },
            'arac-lastik-liste': {
                key: 'arac-lastik-liste',
                label: 'Araç Lastikleri',
                sqlKey: 'arac-lastik-liste',
                formManagerId: 32,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'edit',
                            label: 'Düzenle',
                            type: 'default',
                            icon: 'EditOutlined',
                            onClick: (record) => {
                                const id = record.key_lastik_liste;
                                if (id) {
                                    handleOpenForm(132, 'Araç Lastik', 'arac_lastik', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        }
                    ]
                }
            },
            'arac-zimmet-liste': {
                key: 'arac-zimmet-liste',
                label: 'Araç Zimmetleri',
                sqlKey: 'arac-zimmet-liste',
                formManagerId: 30,
                actionButtons: {
                    secondToLastColumn: [
                        {
                            key: 'add',
                            label: 'Teslimat Ekle',
                            type: 'default',
                            icon: 'PlusOutlined',
                            onClick: (record) => {
                                const id = record.id;
                                if (id) {
                                    handleOpenForm(100, 'Zimmet Teslim', 'zimmet', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'edit',
                            label: 'Düzenle',
                            type: 'default',
                            icon: 'EditOutlined',
                            onClick: (record) => {
                                const id = record.id;
                                if (id) {
                                    handleOpenForm(98, 'Zimmet', 'zimmet', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'view',
                            label: 'Tutanak',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                handleOpenDetailModal(record, null, 'sub-table');
                            }
                        }
                    ],
                    lastColumn: [
                        {
                            key: 'view',
                            label: 'Zimmet',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                handleOpenDetailModal(record, null, 'main-table');
                            }
                        }
                    ]
                }
            },
            'arac-sozlesme-liste': {
                key: 'arac-sozlesme-liste',
                label: 'Araç Sözleşmeleri',
                sqlKey: 'arac-sozlesme-liste',
                formManagerId: 30,
                actionButtons: {
                    lastColumn: [
                        {
                            key: 'edit',
                            label: 'Düzenle',
                            type: 'default',
                            icon: 'EditOutlined',
                            onClick: (record) => {
                                const id = record.key_ceza_liste;
                                if (id) {
                                    handleOpenForm(146, 'Araç Sözleşmeleri', 'arac_sozlesme', 'id', id);
                                } else {
                                    message.error('ID bulunamadı');
                                }
                            }
                        },
                        {
                            key: 'view',
                            label: 'Yükle',
                            type: 'default',
                            icon: 'PlusOutlined',
                            onClick: (record) => {
                                handleOpenDetailModal(record, null, 'sub-table', 'arac-sozlesme');
                            }
                        },
                        {
                            key: 'viewFile',
                            label: 'Dosya Aç',
                            type: 'default',
                            icon: 'EyeOutlined',
                            onClick: (record) => {
                                handleOpenDetailModal(record, null, 'sub-table', 'arac-sozlesme');
                            }
                        }
                    ]
                }
            }
        };

        return configs[keyName] || configs['araclar-satilan'];
    };

    const satilanAraclarData = getDataConfig(currentKeyName);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const currentConfig = getDataConfig(currentKeyName);
            let url, response;
            url = `${API_BASE_URL}/list/execute/${currentConfig.sqlKey}`;
            response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`API Error - Status: ${response.status}, Response:`, errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const allData = await response.json();

            if (!Array.isArray(allData)) {
                console.warn('API response is not an array:', allData);
                setError('Geçersiz veri formatı - API array döndürmüyor');
                return;
            }

            let filteredData = [...allData];

            if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                const filterStartDate = filters.dateRange[0].startOf('day');
                const filterEndDate = filters.dateRange[1].endOf('day');

                filteredData = filteredData.filter(record => {
                    const tarih = record['Alınma Tarihi'] || record['tarih'];

                    if (!tarih) {
                        return false;
                    }

                    let recordDate;
                    const dateValue = String(tarih).trim();

                    if (dateValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                        recordDate = dayjs(dateValue, 'DD.MM.YYYY');
                    } else if (dateValue.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                        recordDate = dayjs(dateValue, 'YYYY.MM.DD');
                    } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        recordDate = dayjs(dateValue, 'YYYY-MM-DD');
                    } else if (dateValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
                        recordDate = dayjs(dateValue, 'DD-MM-YYYY');
                    } else {
                        recordDate = dayjs(tarih);
                    }

                    if (recordDate.isValid()) {
                        const isInRange = recordDate.isBetween(filterStartDate, filterEndDate, null, '[]');
                        return isInRange;
                    }

                    return false;
                });
            }

            if (filters.search && filters.search.trim() !== '') {
                const searchTerm = filters.search.trim();
                filteredData = filteredData.filter(record => {
                    return Object.values(record).some(value => {
                        if (value === null || value === undefined) return false;
                        return trIncludes(String(value), searchTerm);
                    });
                });
            }

            if (filteredData.length > 0) {
                const sample = filteredData[0];
                const dynamicCols = Object.keys(sample)
                    .filter((key) => !key.toLowerCase().includes('_row_style'))
                    .map((key) => {
                        const firstValue = sample[key];
                        const isDateColumn = key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar');
                        const isNumber = typeof firstValue === "number" && !isNaN(firstValue);
                        const uniqueValues = [...new Set(filteredData.map((item) => item[key]))].filter(
                            (val) => val !== undefined && val !== null && val !== ""
                        );

                        let filters = [];
                        if (isNumber) {
                            filters = getNumericFilters();
                        } else if (isDateColumn) {
                            filters = getDateFilters();
                        } else {
                            filters = getStringFilters();
                        }

                        return {
                            key: key,
                            title: key,
                            dataIndex: key,
                            isDateColumn,
                            isNumber,
                            isString: !isNumber && !isDateColumn,
                            uniqueValues,
                            sortable: true,
                            filters, 
                        };
                    });
                setColumns(dynamicCols);
            }

            setData(filteredData || []);
            setShowData(filteredData || []);
            setPagination(prev => ({
                ...prev,
                total: filteredData?.length || 0
            }));

        } catch (err) {
            console.error('Satılan Araçlar data load error:', err);
            setError('Veri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, [filters.search, filters.dateRange, currentKeyName]);

    const handlePivotRedirect = useCallback(() => {
        const config = getDataConfig(currentKeyName);
        if (!config?.sqlKey) return;
        redirectToPivotWithData({
            data,
            filters,
            tabLabel: config.label || 'Satılan Araçlar',
            sqlKey: config.sqlKey,
            navigate,
            message,
            setLoading,
            apiBaseUrl: API_BASE_URL,
        });
    }, [currentKeyName, data, filters, navigate]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));

        if (key === 'dateRange') {
            setTimeout(() => {
                loadData();
            }, 100);
        }

        if (key === 'search') {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }

            const newTimeout = setTimeout(() => {
                loadData();
            }, 500);

            setSearchTimeout(newTimeout);
        }
    };

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize
        }));
    };

    const handleAdvancedFilterChange = (columnKey, valueType, value) => {
        setFilteredInfo((prev) => ({
            ...prev,
            [columnKey]: {
                ...prev[columnKey],
                [valueType]: value,
            },
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearColumnFilter = (columnKey) => {
        setFilteredInfo((prev) => {
            const newFilteredInfo = { ...prev };
            delete newFilteredInfo[columnKey];
            return newFilteredInfo;
        });
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setFilters({
            dateRange: null,
            search: '',
            page: 1,
            pageSize: 50
        });
        setSortedInfo({});
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    }, []);

    const handleSort = (columnKey) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column) return;

        let newOrder = "asc";
        if (sortedInfo.columnKey === columnKey) {
            if (sortedInfo.order === "asc") {
                newOrder = "desc";
            } else if (sortedInfo.order === "desc") {
                newOrder = null;
            }
        }

        setSortedInfo({
            columnKey: newOrder ? columnKey : null,
            order: newOrder,
        });
    };

    useEffect(() => {
        if (currentKeyName) {
            loadData();
        }
    }, [currentKeyName, loadData]);

    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);
        setCurrentUniqueValue(uniqueValue);
        setFormModalOpen(true);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        loadData();
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
    };

    const handleOpenDetailModal = async (record, firstChar = null, modalType = 'main-table', fileType = null) => {
        const id = record.key_demirbas_zimmet || record.id || record.No || record.key_kaza_liste || record.key_ceza_liste || record.key_ariza_liste;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }

        const idString = String(id);
        setCurrentRecordId(idString);
        setDetailModalOpen(true);
        setDetailLoading(true);
        setDetailError(null);
        setModalButtonType(modalType);

        try {
            const charToUse = firstChar || idString.charAt(0).toLowerCase();
            let queryName = 'demirbas';
            let modalTitle = 'Araç Detayları';

            if (modalType === 'sub-table') {
                if (fileType) {
                    queryName = 'arac-dosyalar';
                    const fileTypeLabels = {
                        'arac-ruhsat': 'Ruhsat',
                        'arac-ariza': 'Arıza',
                        'arac-bakim': 'Bakım',
                        'arac-sozlesme': 'Sözleşme',
                        'arac-ceza': 'Ceza',
                        'arac-kaza': 'Kaza',
                        'arac-lastik': 'Lastik',
                        'arac-muayene': 'Muayene',
                        'arac-sigorta': 'Sigorta',
                        'arac-tahsis': 'Tahsis',
                        'arac-vergi': 'Vergi',
                        'arac-yedek': 'Yedek',
                        'arac-aksesuar': 'Aksesuar'
                    };
                    modalTitle = `${fileTypeLabels[fileType] || fileType} Dosyaları`;
                } else {
                    queryName = 'demirbas';
                    modalTitle = 'Araç Detayları';
                }
            } else {
                switch (charToUse) {
                    case 'k':
                        modalTitle = 'Kredi Kartı Zimmet Detayları';
                        queryName = 'zimmet-detail-k';
                        break;
                    case 'a':
                        modalTitle = 'Araç Zimmet Detayları';
                        queryName = 'zimmet-detail-a';
                        break;
                    case 'd':
                        modalTitle = 'Araç Zimmet Detayları';
                        queryName = 'zimmet-detail-d';
                        break;
                    default:
                        queryName = 'demirbas';
                        modalTitle = 'Araç Detayları';
                        break;
                }
            }

            let paramId = idString;
            let requestParams = { id: paramId };

            if (modalType === 'sub-table' && queryName === 'demirbas') {
                paramId = record.demirbas_id || record.id || idString;
                requestParams = { id: paramId };
            } else if (modalType === 'sub-table' && fileType) {
                const cleanId = idString.replace(/^[akd]/, '');
                requestParams = { id: cleanId, dosya_tipi: fileType };
            }

            const response = await fetch(`${API_BASE_URL}/list/execute-query/${queryName}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ params: requestParams })
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDetailData(Array.isArray(data) ? data : []);
            setModalTitle(modalTitle);
        } catch (err) {
            console.error('Detail modal data load error:', err);
            setDetailError('Detay verileri yüklenirken hata oluştu: ' + err.message);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetailModal = () => {
        setDetailModalOpen(false);
        setDetailData([]);
        setDetailError(null);
        setCurrentRecordId(null);
        setModalButtonType('main-table');
    };

    const handleViewFile = (record) => {
        const filePath = record.file;
        if (filePath) {
            const fileUrl = `${API_BASE_URL}/files/${filePath}`;
            window.open(fileUrl, '_blank');
        } else {
            message.warning('Dosya bulunamadı');
        }
    };

    const handleEditFile = (record) => {
        const id = record.No;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        handleOpenForm(98, 'Demirbaş Dosya Düzenle', 'zimmet', 'id', id);
    };

    const handleSubEditFile = (record) => {
        const id = record.dosyaID;
        if (!id) {
            message.error('Dosya ID bulunamadı');
            return;
        }
        const fileType = record.root;
        const formIds = {
            'arac-ruhsat': 110,
            'arac-ariza': 120,
            'arac-bakim': 116,
            'arac-sozlesme': 108,
            'arac-ceza': 118,
            'arac-kaza': 116,
            'arac-muayene': 121,
            'arac-sigorta': 122,
            'arac-tahsis': 123,
            'arac-vergi': 124,
            'arac-yedek': 125,
            'arac-aksesuar': 126
        };

        const formId = formIds[fileType] || 109;
        const formTitle = `${fileType.replace('arac-', '').toUpperCase()} DOSYA DÜZENLE`;

        handleOpenForm(formId, formTitle, 'araclar_dosya', 'id', id);
    };

    const handleAddDelivery = (record) => {
        const id = record.No;
        if (!id) {
            message.error('ID bulunamadı');
            return;
        }
        handleOpenForm(100, 'Zimmet Teslim Formu', 'zimmet', 'id', id);
    };

    const filteredAndSortedData = useMemo(() => {
        let result = data.filter((item) => {
            const matchesSearch = Object.values(item).some((val) =>
                trIncludes(String(val), filters.search)
            );

            const matchesFilters = Object.entries(filteredInfo).every(([key, filter]) => {
                const recordValue = item[key];
                const col = columns.find((c) => c.key === key);
                return applyAdvancedFilter(recordValue, filter, col);
            });

            return matchesSearch && matchesFilters;
        });

        if (sortedInfo.columnKey && sortedInfo.order) {
            const column = columns.find((col) => col.key === sortedInfo.columnKey);
            if (column) {
                result.sort((a, b) => {
                    const valA = a[sortedInfo.columnKey];
                    const valB = b[sortedInfo.columnKey];

                    if (column.isDateColumn) {
                        let dateA = null, dateB = null;
                        
                        if (valA) {
                            const dateValueA = String(valA).trim();
                            if (dateValueA === '0000-00-00' || dateValueA === '0000-00-00 00:00:00' || dateValueA === '1900-01-01' || dateValueA === '' || dateValueA === 'null' || dateValueA === 'undefined') {
                                dateA = null; 
                            } else if (dateValueA.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD.MM.YYYY');
                            } else if (dateValueA.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY-MM-DD');
                            } else if (dateValueA.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY.MM.DD');
                            } else if (dateValueA.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD-MM-YYYY');
                            } else if (dateValueA.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
                                dateA = dayjs(dateValueA, 'YYYY/MM/DD');
                            } else if (dateValueA.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                dateA = dayjs(dateValueA, 'DD/MM/YYYY');
                            } else {
                                dateA = dayjs(valA);
                            }
                        }
                        
                        if (valB) {
                            const dateValueB = String(valB).trim();
                            if (dateValueB === '0000-00-00' || dateValueB === '0000-00-00 00:00:00' || dateValueB === '1900-01-01' || dateValueB === '' || dateValueB === 'null' || dateValueB === 'undefined') {
                                dateB = null; 
                            } else if (dateValueB.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD.MM.YYYY');
                            } else if (dateValueB.match(/^\d{4}-\d{2}-\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY-MM-DD');
                            } else if (dateValueB.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY.MM.DD');
                            } else if (dateValueB.match(/^\d{2}-\d{2}-\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD-MM-YYYY');
                            } else if (dateValueB.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
                                dateB = dayjs(dateValueB, 'YYYY/MM/DD');
                            } else if (dateValueB.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                dateB = dayjs(dateValueB, 'DD/MM/YYYY');
                            } else {
                                dateB = dayjs(valB);
                            }
                        }
                        
                        if (!dateA && !dateB) return 0;
                        if (!dateA || !dateA.isValid()) return sortedInfo.order === "asc" ? -1 : 1;
                        if (!dateB || !dateB.isValid()) return sortedInfo.order === "asc" ? 1 : -1;
                        const comparison = dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    } else if (column.isNumber) {
                        const numA = Number(valA) || 0;
                        const numB = Number(valB) || 0;
                        return sortedInfo.order === "asc" ? numA - numB : numB - numA;
                    } else {
                        const strA = String(valA || "").toLowerCase();
                        const strB = String(valB || "").toLowerCase();
                        const comparison = strA.localeCompare(strB, 'tr');
                        return sortedInfo.order === "asc" ? comparison : -comparison;
                    }
                });
            }
        }

        return result;
    }, [data, filters.search, filteredInfo, sortedInfo, columns]);

    const paginatedData = useMemo(() => {
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        return filteredAndSortedData.slice(startIndex, startIndex + pagination.pageSize);
    }, [filteredAndSortedData, pagination.current, pagination.pageSize]);

    const getSortIcon = (columnKey) => {
        if (sortedInfo.columnKey === columnKey) {
            return sortedInfo.order === "asc" ? <CaretUpOutlined /> : <CaretDownOutlined />;
        }
        return null;
    };

    const removeColumn = (columnKey) => {
        setColumns((prevCols) => prevCols.filter((col) => col.key !== columnKey));
    };

    const getFilterDropdown = (column) => {
        const filter = filteredInfo[column.key] || {};
        const isActive = filter.condition && filter.condition !== "choose";

        return (
            <div style={{ padding: 8, minWidth: 200 }}>
                <Select
                    placeholder="Filtre Seçin"
                    value={filter.condition || "choose"}
                    onChange={(value) => handleAdvancedFilterChange(column.key, "condition", value)}
                    style={{ width: "100%", marginBottom: 8 }}
                    className={darkMode ? 'dark-select' : ''}
                >
                    {column.filters.map((filter) => (
                        <Option key={filter.value} value={filter.value}>
                            {filter.text}
                        </Option>
                    ))}
                </Select>

                {filter.condition && filter.condition !== "choose" && (
                    <>
                        {column.isNumber && filter.condition === "between" ? (
                            <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                                <InputNumber
                                    placeholder="Min"
                                    value={filter.min}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "min", value)}
                                    style={{ flex: 1 }}
                                    className={darkMode ? 'dark-input' : ''}
                                />
                                <InputNumber
                                    placeholder="Max"
                                    value={filter.max}
                                    onChange={(value) => handleAdvancedFilterChange(column.key, "max", value)}
                                    style={{ flex: 1 }}
                                    className={darkMode ? 'dark-input' : ''}
                                />
                            </div>
                        ) : column.isDateColumn && filter.condition === "between" ? (
                            <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                                <DatePicker
                                    placeholder="Başlangıç"
                                    value={filter.startDate ? dayjs(filter.startDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "startDate", date)}
                                    format="DD.MM.YYYY"
                                    style={{ flex: 1 }}
                                    className={darkMode ? 'dark-date-picker' : ''}
                                />
                                <DatePicker
                                    placeholder="Bitiş"
                                    value={filter.endDate ? dayjs(filter.endDate, 'DD.MM.YYYY') : null}
                                    onChange={(date) => handleAdvancedFilterChange(column.key, "endDate", date)}
                                    format="DD.MM.YYYY"
                                    style={{ flex: 1 }}
                                    className={darkMode ? 'dark-date-picker' : ''}
                                />
                            </div>
                        ) : column.isDateColumn && filter.condition !== "thisMonth" && filter.condition !== "thisYear" && filter.condition !== "lastMonth" && filter.condition !== "lastYear" ? (
                            <DatePicker
                                placeholder="Tarih Seçin"
                                value={filter.value ? dayjs(filter.value, 'DD.MM.YYYY') : null}
                                onChange={(date) => handleAdvancedFilterChange(column.key, "value", date)}
                                format="DD.MM.YYYY"
                                style={{ width: "100%", marginBottom: 8 }}
                                className={darkMode ? 'dark-date-picker' : ''}
                            />
                        ) : filter.condition === "select" ? (
                            <Select
                                placeholder="Değer Seçin"
                                value={filter.value}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                                style={{ width: "100%", marginBottom: 8 }}
                                className={darkMode ? 'dark-select' : ''}
                            >
                                {column.uniqueValues.map((val) => (
                                    <Option key={val} value={val}>
                                        {val}
                                    </Option>
                                ))}
                            </Select>
                        ) : !["empty", "notEmpty", "thisMonth", "thisYear", "lastMonth", "lastYear"].includes(filter.condition) ? (
                            <Input
                                placeholder="Değer Girin"
                                value={filter.value || ""}
                                onChange={(e) => handleAdvancedFilterChange(column.key, "value", e.target.value)}
                                style={{ width: "100%", marginBottom: 8 }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        ) : null}

                        <div style={{ display: "flex", gap: 4 }}>
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => {
                                    setFilteredInfo((prev) => ({
                                        ...prev,
                                        [column.key]: { ...filter, applied: true },
                                    }));
                                }}
                                style={{ flex: 1 }}
                                className={darkMode ? 'dark-button' : ''}
                            >
                                Uygula
                            </Button>
                            <Button
                                size="small"
                                onClick={() => clearColumnFilter(column.key)}
                                style={{ flex: 1 }}
                                className={darkMode ? 'dark-button' : ''}
                            >
                                Temizle
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className={`kurulum-page ${darkMode ? 'dark-theme' : ''}`}>
            {/* Filtreler */}
            <Card
                className={`kurulum-filters-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                {/* Başlık - En üstte */}
                <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Title level={3} style={{
                            color: darkMode ? 'var(--dark-text)' : undefined,
                            margin: 0,
                            textAlign: 'center'
                        }}>
                            {satilanAraclarData.label}
                            <EditOutlined onClick={() => handleOpenForm(275, 'Liste Formu', 'sqls', 'id', satilanAraclarData.formManagerId)} />
                        </Title>
                    </Col>
                </Row>

                {/* Filtreler */}
                <Row gutter={[16, 16]} align="middle">
                    {/* Tarih Aralığı - En solda */}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Tarih Aralığı
                            </Text>
                            <RangePicker
                                value={filters.dateRange}
                                onChange={(dates) => handleFilterChange('dateRange', dates)}
                                style={{
                                    width: '100%',
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                placeholder={['Başlangıç', 'Bitiş']}
                                format="DD.MM.YYYY"
                                className={darkMode ? 'dark-date-picker' : ''}
                                locale={{
                                    lang: {
                                        locale: 'tr_TR',
                                        placeholder: 'Tarih seçin',
                                        rangePlaceholder: ['Başlangıç', 'Bitiş'],
                                        today: 'Bugün',
                                        now: 'Şimdi',
                                        backToToday: 'Bugüne dön',
                                        ok: 'Tamam',
                                        clear: 'Temizle',
                                        month: 'Ay',
                                        year: 'Yıl',
                                        timeSelect: 'Zaman seç',
                                        dateSelect: 'Tarih seç',
                                        monthSelect: 'Ay seç',
                                        yearSelect: 'Yıl seç',
                                        decadeSelect: 'On yıl seç',
                                        yearFormat: 'YYYY',
                                        dateFormat: 'DD.MM.YYYY',
                                        dayFormat: 'DD',
                                        dateTimeFormat: 'DD.MM.YYYY HH:mm:ss',
                                        monthFormat: 'MMMM',
                                        monthBeforeYear: true,
                                        previousMonth: 'Önceki ay (PageUp)',
                                        nextMonth: 'Sonraki ay (PageDown)',
                                        previousYear: 'Önceki yıl (Control + left)',
                                        nextYear: 'Sonraki yıl (Control + right)',
                                        previousDecade: 'Önceki on yıl',
                                        nextDecade: 'Sonraki on yıl',
                                        previousCentury: 'Önceki yüzyıl',
                                        nextCentury: 'Sonraki yüzyıl',
                                    },
                                    timePickerLocale: {
                                        placeholder: 'Zaman seçin',
                                    },
                                }}
                            />
                        </div>
                    </Col>

                    {/* Arama - Tarih yanında */}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Arama
                            </Text>
                            <Input
                                placeholder="Arama yapın..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                prefix={<SearchOutlined style={{ color: darkMode ? 'var(--dark-text-muted)' : undefined }} />}
                                allowClear
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    </Col>

                    {/* Aksiyon Butonları - Sağ tarafta */}
                    <Col xs={24} sm={24} md={12} lg={16}>
                        <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Space wrap>
                                <Button
                                    onClick={clearAllFilters}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Temizle
                                </Button>
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'excel',
                                                icon: <FileExcelOutlined />,
                                                label: 'Excel (.xlsx)',
                                                onClick: () => exportToExcel(filteredAndSortedData)
                                            },
                                            {
                                                key: 'print',
                                                icon: <PrinterOutlined />,
                                                label: 'Yazdır',
                                                onClick: () => printTable(filteredAndSortedData, { title: satilanAraclarData?.label || 'Satılan Araçlar', message })
                                            }
                                        ]
                                    }}
                                    trigger={['click']}
                                >
                                    <Button
                                        icon={<DownloadOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Dışa Aktar
                                    </Button>
                                </Dropdown>
                                {currentKeyName === 'araclar-satilan' && (
                                    <>
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            onClick={() => navigate('/araclar')}
                                            loading={loading}
                                            className={darkMode ? 'dark-button' : ''}
                                        >
                                            Araçlar Listesi
                                        </Button>
                                    </>
                                )}
                                
                                {currentKeyName === 'bitmis-sigorta' && (
                                    <>
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            onClick={() => navigate('/araclar')}
                                            loading={loading}
                                            className={darkMode ? 'dark-button' : ''}
                                        >
                                            Sigorta Listesi
                                        </Button>
                                    </>
                                )}
                                {currentKeyName === 'tum_sigortalar' && (
                                    <>
                                        <Button
                                            type="default"
                                            loading={loading}
                                            className={darkMode ? 'dark-button' : ''}
                                        >
                                            Firma Sigortaları
                                        </Button>
                                        <Button
                                            type="default"
                                            loading={loading}
                                            className={darkMode ? 'dark-button' : ''}
                                        >
                                            Araç Sigortaları
                                        </Button>
                                    </>
                                )}
                            </Space>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Tablo */}
            <Card
                className={`kurulum-content-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <div>
                    {/* Aksiyon Butonları */}
                    <div className="kurulum-actions" style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button
                                icon={<BarChartOutlined />}
                                onClick={handlePivotRedirect}
                                disabled={loading || data.length === 0}
                                className={darkMode ? 'dark-button' : ''}
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                            >
                                Pivot Tabloya Git
                            </Button>
                        </Space>
                    </div>

                    {/* Tablo */}
                    {error && (
                        <Alert
                            message="Hata"
                            description={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Table
                        dataSource={paginatedData}
                        loading={loading}
                        rowKey={(record) => record['Platform No']}
                        scroll={{ x: 'max-content' }}
                        className={darkMode ? 'dark-table' : ''}
                        rowClassName={(record) => {
                            const bitisTarihiKey = Object.keys(record).find(key => 
                                key.toLowerCase().includes('bitiş tarihi') 
                            );
                            
                            if (bitisTarihiKey) {
                                if (isDateExpired(bitisTarihiKey, record[bitisTarihiKey])) {
                                    return 'expired-row';
                                }
                                
                                if (isDateApproaching(bitisTarihiKey, record[bitisTarihiKey])) {
                                    return 'approaching-row';
                                }
                            }
                            
                            return '';
                        }}
                        pagination={{
                            current: pagination.current,
                            total: filteredAndSortedData.length,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} / ${total} kayıt`,
                            onChange: handlePageChange,
                            onShowSizeChange: handlePageChange,
                            pageSizeOptions: ['10', '25', '50', '100']
                        }}
                        size="small"
                        style={{
                            width: '100%',
                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                        }}
                        columns={columns.map((column) => {
                            const isActive = filteredInfo[column.key] && filteredInfo[column.key].condition && filteredInfo[column.key].condition !== "choose";
                            
                            return {
                                ...column,
                                title: (
                                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <Tooltip title="Sütunu Kaldır">
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<ClearOutlined />}
                                                onClick={() => removeColumn(column.key)}
                                                style={{ 
                                                    color: darkMode ? 'var(--dark-text-muted)' : '#999',
                                                    padding: '2px 4px',
                                                    minWidth: 'auto',
                                                    height: 'auto'
                                                }}
                                                className={darkMode ? 'dark-button' : ''}
                                            />
                                        </Tooltip>
                                        <span style={{ flex: 1 }}>{column.title}</span>
                                        {column.sortable && (
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={getSortIcon(column.key)}
                                                onClick={() => handleSort(column.key)}
                                                style={{ 
                                                    color: darkMode ? 'var(--dark-text-muted)' : '#999',
                                                    padding: '2px 4px',
                                                    minWidth: 'auto',
                                                    height: 'auto'
                                                }}
                                                className={darkMode ? 'dark-button' : ''}
                                            />
                                        )}
                                        <Dropdown
                                            overlay={getFilterDropdown(column)}
                                            trigger={['click']}
                                            placement="bottomRight"
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<FilterOutlined />}
                                                style={{ 
                                                    color: isActive ? '#1890ff' : (darkMode ? 'var(--dark-text-muted)' : '#999'),
                                                    padding: '2px 4px',
                                                    minWidth: 'auto',
                                                    height: 'auto'
                                                }}
                                                className={darkMode ? 'dark-button' : ''}
                                            />
                                        </Dropdown>
                                    </div>
                                ),
                                render: (text, record) => {
                                    const totalColumns = columns.length;
                                    const isLastColumn = columns.indexOf(column) === totalColumns - 1;
                                    const isSecondToLastColumn = columns.indexOf(column) === totalColumns - 2;
                                    const hasActionButtons = satilanAraclarData && satilanAraclarData.actionButtons;
                                    const hasSecondToLastButtons = hasActionButtons && satilanAraclarData.actionButtons.secondToLastColumn && satilanAraclarData.actionButtons.secondToLastColumn.length > 0;
                                    const hasLastButtons = hasActionButtons && satilanAraclarData.actionButtons.lastColumn && satilanAraclarData.actionButtons.lastColumn.length > 0;

                                    const isSigortaListeColumn = currentKeyName === 'arac-bitmis_sigorta-liste' && column.key === 'key_sigorta_liste';
                                    
                                    const shouldShowActionButtons = isSigortaListeColumn || (isSecondToLastColumn && hasSecondToLastButtons) || (isLastColumn && hasLastButtons);
                                    const actionButtonsToShow = isSigortaListeColumn ? 
                                        (satilanAraclarData.actionButtons?.secondToLastColumn || []) :
                                        (isSecondToLastColumn ?
                                            (satilanAraclarData.actionButtons?.secondToLastColumn || []) :
                                            (satilanAraclarData.actionButtons?.lastColumn || []));

                                    return (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {shouldShowActionButtons ? (
                                                <ActionButtons
                                                    record={record}
                                                    actionButtons={actionButtonsToShow}
                                                    darkMode={darkMode}
                                                />
                                            ) : (
                                                <span>{formatCellValue(column.key, text)}</span>
                                            )}
                                        </div>
                                    );
                                }
                            };
                        })}
                    />
                </div>
            </Card>

            {/* Form Modal */}
            {formModalOpen && (
                <Modal
                    title={modalTitle}
                    open={formModalOpen}
                    onCancel={closeFormModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={800}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        <FormikForm
                            formId={currentFormId}
                            uniqueValue={currentUniqueValue}
                            uniqueField={formUniqueField}
                            tableName={formTableName}
                            onSuccess={handleFormSuccess}
                        />
                    </div>
                </Modal>
            )}

            {/* Detail Modal */}
            {detailModalOpen && (
                <Modal
                    title={modalTitle}
                    open={detailModalOpen}
                    onCancel={closeDetailModal}
                    footer={null}
                    destroyOnHidden={true}
                    width={1200}
                    style={{ top: 20 }}
                >
                    <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                        {detailError && (
                            <Alert
                                message="Hata"
                                description={detailError}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <Table
                            dataSource={detailData}
                            loading={detailLoading}
                            rowKey={(record) => record.dosyaID || record.id || Math.random()}
                            scroll={{ x: 'max-content' }}
                            className={darkMode ? 'dark-table' : ''}
                            size="small"
                            style={{
                                width: '100%',
                                backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                            }}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} / ${total} kayıt`,
                                pageSizeOptions: ['10', '25', '50', '100']
                            }}
                        >
                            {detailData && detailData.length > 0 && Object.keys(detailData[0]).map((key, index) => {
                                const getColumnWidth = (key) => {
                                    const keyLength = key.length;

                                    if (key.toLowerCase().includes('tarih') || key.toLowerCase().includes('tar')) {
                                        return Math.max(120, keyLength * 8);
                                    }
                                    if (key.toLowerCase().includes('tutar') || key.toLowerCase().includes('fiyat') || key.toLowerCase().includes('ücret')) {
                                        return Math.max(120, keyLength * 8);
                                    }
                                    if (keyLength > 20) {
                                        return Math.max(200, keyLength * 8);
                                    }
                                    if (keyLength > 10) {
                                        return Math.max(120, keyLength * 8);
                                    }
                                    return Math.max(80, keyLength * 8);
                                };

                                const totalColumns = Object.keys(detailData[0]).length;
                                const isLastColumn = index === totalColumns - 1;

                                return (
                                    <Table.Column
                                        key={key}
                                        title={key}
                                        dataIndex={key}
                                        width={isLastColumn ? Math.max(getColumnWidth(key), 200) : getColumnWidth(key)}
                                        ellipsis={{
                                            showTitle: true
                                        }}
                                        render={(text, record) => (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {isLastColumn ? (
                                                    <Space size="small">
                                                        {modalButtonType === 'main-table' ? (
                                                            <>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<EditOutlined />}
                                                                    onClick={() => {
                                                                        handleEditFile(record);
                                                                    }}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Düzenle
                                                                </Button>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<PlusOutlined />}
                                                                    onClick={() => handleAddDelivery(record)}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Teslimat Ekle
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<EyeOutlined />}
                                                                    onClick={() => handleViewFile(record)}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Görüntüle
                                                                </Button>
                                                                <Button
                                                                    type="default"
                                                                    size="small"
                                                                    icon={<EditOutlined />}
                                                                    onClick={() => {
                                                                        handleSubEditFile(record);
                                                                    }}
                                                                    className={darkMode ? 'dark-button' : ''}
                                                                    style={{
                                                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                                        color: darkMode ? 'var(--dark-text)' : undefined
                                                                    }}
                                                                >
                                                                    Düzenle
                                                                </Button>
                                                            </>
                                                        )}
                                                    </Space>
                                                ) : (
                                                    <span>{formatCellValue(key, text)}</span>
                                                )}
                                            </div>
                                        )}
                                        sorter={(a, b) => {
                                            const aVal = a[key];
                                            const bVal = b[key];
                                            if (!isNaN(aVal) && !isNaN(bVal)) {
                                                return Number(aVal) - Number(bVal);
                                            }
                                            return String(aVal).localeCompare(String(bVal), 'tr');
                                        }}
                                    />
                                );
                            })}
                        </Table>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default SatilanAraclarPage;